import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Link } from "react-router-dom";
import queryString from 'query-string';

import api from '@/services/api';

import Pagination from '@/utils/pagination/Pagination';

import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';
import LettersSearchFilters from '@/views/pages/Letters/elements/LettersSearchFilters';
import LettersContainer from '@/views/pages/Letters/elements/LettersContainer';

import LetterContextWrapper from '@/views/pages/Letter/elements/LetterContextWrapper';
import LetterLogic from '@/views/pages/Letter/LetterLogic';
import EditLetterWrapper from '@/views/pages/Letter/elements/EditLetterWrapper';

import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import { CheckPermission, CanHandleDepartments } from '@/utils/permissions/permissions';

import { appActions } from '@/store/actions/app.actions';

import styles from '@/views/pages/Letters/Letters.module.scss';

const Letters = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { dict, mode } = useSelector((state) => state.app);

	const { user: { role: userRole } } = useSelector((state) => state.auth);

	const location = useLocation();

	const [ lettersData, setLettersData ] = useState(null);
	const [ pagesNum, setPagesNum ] = useState(1);
	const [ lettersNum, setLettersNum ] = useState(0);

	const [ canCreateLetter, setCanCreateLetter ] = useState(false);
	
	const [ sortMethods, setSortMethods ] = useState([
		{ value: '-modifiedOn', label: dict.sortOptions.lastModified },
		{ value: 'title', label: dict.sortOptions.title },
		{ value: '-title', label: dict.sortOptions.titleReverse },
		CanHandleDepartments(userRole, mode) && { value: 'department', label: dict.sortOptions.department },
		CanHandleDepartments(userRole, mode) && { value: '-department', label: dict.sortOptions.departmentReverse },
	].filter(Boolean));

	const [ sortOn, setSortOn ] = useState(sortMethods[0]);
	const [ isLoading, setIsLoading ] = useState(true);

	const { search: locationSearch, pathname: locationPathName } = location;

	const changeSort = useCallback((element) => {
		const searchParams = queryString.parse(locationSearch);
		const copiedSearchParams = Object.assign({}, searchParams);

		setSortOn(element);
		navigate(`${locationPathName}?${parseObjectToQuery({ ...copiedSearchParams, ord: element.value })}`);
	}, [ setSortOn, locationSearch, locationPathName ]);

	useEffect(() => {
		if (CheckPermission("canCreateLetter", userRole)) {
			return setCanCreateLetter(true);
		}
		setCanCreateLetter(false);
	}, [ userRole ]);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);

		if (copiedSearchParams.q) {
			const newSortMethods = [
				{ value: '-accuracy', label: dict.sortOptions.accuracy },
				{ value: '-modifiedOn', label: dict.sortOptions.lastModified },
				{ value: 'title', label: dict.sortOptions.title },
				{ value: '-title', label: dict.sortOptions.titleReverse },
				CanHandleDepartments(userRole, mode) && { value: 'department', label: dict.sortOptions.department },
				CanHandleDepartments(userRole, mode) && { value: '-department', label: dict.sortOptions.departmentReverse },
			].filter(Boolean);
			setSortMethods(newSortMethods);
		} else {
			const newSortMethods = [
				{ value: '-modifiedOn', label: dict.sortOptions.lastModified },
				{ value: 'title', label: dict.sortOptions.title },
				{ value: '-title', label: dict.sortOptions.titleReverse },
				CanHandleDepartments(userRole, mode) && { value: 'department', label: dict.sortOptions.department },
				CanHandleDepartments(userRole, mode) && { value: '-department', label: dict.sortOptions.departmentReverse },
			].filter(Boolean);
			setSortMethods(newSortMethods);
		}
	}, [ location, dict ]);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);

		if (copiedSearchParams.ord) {
			setSortOn(sortMethods.find((el) => { return el.value === copiedSearchParams.ord; }));
		} else {
			setSortOn(sortMethods[0]);
		}
	}, [ sortMethods ]);

	const fetchData = ({ data }) => {
		if (data) {
			setLettersData(data.letters);
			setPagesNum(parseInt(data.pages));
			setLettersNum(data.hits);
		}
	};

	const reloadLettersList = () => {
		const searchParams = queryString.parse(location.search);

		setIsLoading(true);
		dispatch(appActions.setLastLettersURL(`/letters${location.search}`));
		api.get(`/letters?${parseObjectToQuery(searchParams)}`).then(res => {
			fetchData(res);
			setIsLoading(false);
		});
	};

	useEffect(() => {
		// if there is no page in location don't fetch data
		reloadLettersList();
	}, [ location ]);

	return (
		<div className={ styles['letters__wrapper'] }>
			<Header narrow={ false } />
			<LetterContextWrapper>
				<LetterLogic>
					<EditLetterWrapper />
					<main className={ styles['letters__main'] }>
						<h1 className={ styles['letters__header'] }>{dict.letters.letters}
							{lettersNum > 0 && (
								<span className={ styles['letters__header--number'] }>&nbsp;({lettersNum})</span>)}
						</h1>
						<div className={ styles['letters__description'] }>
							<div className={ styles['letters__description--text'] }>
								{ userRole === 'reader' && dict.letters.description}
								{ userRole === 'editor' && dict.letters.descriptionRedactor}
								{ (userRole === 'admin' || userRole === 'expert') && dict.letters.descriptionAdministrator}
							</div>
							{canCreateLetter && (<div className={ styles['letters__description__addnewletter-btn'] }>
								<Link
									className= { `${styles['letters__description__addnewletter-btn--button']} button__blue` }
									to="/add-letter"
								>+ {dict.addLetter.addLetter}
								</Link>
							</div>
							)}
						</div>
						<LettersSearchFilters />
						<Pagination
							pagesNum={ pagesNum }
							sortingOptions={ sortMethods }
							sortingValue={ sortOn }
							sortingOnChange={ changeSort }
						>
							<LettersContainer
								lettersData={ lettersData }
								isLoading={ isLoading }
								reloadLettersList={ reloadLettersList }
							/>
						</Pagination>
					</main>
				</LetterLogic>
			</LetterContextWrapper>
			<Footer narrow={ false } />
		</div>

	);
};
export default Letters;
